import Breadcrumb from "Components/organisms/breadcrumb"
import LoadingState from "Components/organisms/loading-state"
import InfoTip from "Components/atoms/info-tip"
import { GrossMarginReport } from "models/gross-margin-report"
import { SECTION_INFO } from "data/gross-margin-sources"
import { useMemo } from "react"
import { Container } from "reactstrap"

export type GrossMarginValueKind = "actual" | "budget" | "variance" | "variancePct"

// Clicked value: the report field without Actual/Budget (e.g. "consultingIncome")
export interface GrossMarginValueClick {
    field: string
    label: string
    kind: GrossMarginValueKind
    value: number | null
    isPercent: boolean
}

interface GrossMarginReportTemplateProps {
    title: string
    breadcrumbItem: string
    actions?: React.ReactNode
    tableActions?: React.ReactNode
    filters?: React.ReactNode
    modal?: React.ReactNode
    isLoading: boolean
    report: GrossMarginReport | null
    // Every value is clickable: the page explains where it comes from
    onValueClick?: (click: GrossMarginValueClick) => void
}

// -------------------------
// Helpers
// -------------------------
const fmt = (val: number | null | undefined) =>
    val == null ? '0.00' : val.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtPct = (val: number | null | undefined) =>
    val == null ? '0.00' : (val * 100).toFixed(2) + '%'

const variance = (a: number | null, b: number | null) => (a ?? 0) - (b ?? 0)
const variancePct = (a: number | null, b: number | null) => b ? ((a ?? 0) - (b ?? 0)) / Math.abs(b) : null

const negPos = (v: number | null) => v == null ? '' : v < 0 ? 'gmr-neg' : v > 0 ? 'gmr-pos' : ''

// -------------------------
// Row
// -------------------------
type RowVariant = 'row' | 'total' | 'net' | 'grand' | 'pct'

interface RowProps {
    label: string
    field: string
    actual?: number | null
    budget?: number | null
    indent?: number
    variant?: RowVariant
    onValueClick?: (click: GrossMarginValueClick) => void
}

const ROW_CLASS: Record<RowVariant, string> = {
    row: 'gmr-row', total: 'gmr-total', net: 'gmr-net', grand: 'gmr-grand', pct: 'gmr-pct',
}

const Row = ({ label, field, actual = null, budget = null, indent = 1, variant = 'row', onValueClick }: RowProps) => {
    const isPct = variant === 'pct'
    const show = isPct ? fmtPct : fmt
    const colored = variant !== 'net' && variant !== 'pct'
    const diff = isPct ? (actual ?? 0) - (budget ?? 0) : variance(actual, budget)
    const diffPct = variancePct(actual, budget)

    const cell = (kind: GrossMarginValueKind, value: number | null, text: string, className: string) => (
        <td
            className={`${className} ${onValueClick ? 'gmr-clickable' : ''}`.trim()}
            onClick={onValueClick ? () => onValueClick({ field, label, kind, value, isPercent: isPct || kind === 'variancePct' }) : undefined}
            title={onValueClick ? 'Click to see where this value comes from' : undefined}
        >
            {text}
        </td>
    )

    return (
        <tr className={ROW_CLASS[variant]}>
            <td style={variant === 'row' ? { paddingLeft: `${indent * 16}px` } : undefined}>{label}</td>
            {cell('actual', actual, show(actual), colored ? negPos(actual) : '')}
            {cell('budget', budget, show(budget), colored ? negPos(budget) : '')}
            {cell('variance', diff, isPct ? fmtPct(diff) : fmt(diff), negPos(isPct ? diffPct : diff))}
            {cell('variancePct', diffPct, fmtPct(diffPct), negPos(diffPct))}
        </tr>
    )
}

const SectionTitle = ({ id, label, className = 'gmr-section' }: { id: string, label: string, className?: string }) => (
    <tr className={className}>
        <td colSpan={5}>
            <span className="d-inline-flex align-items-center gap-1">
                {label} {SECTION_INFO[id] && <InfoTip id={`gmr-${id}`} text={SECTION_INFO[id]} />}
            </span>
        </td>
    </tr>
)

// -------------------------
// Template
// -------------------------
const GrossMarginReportTemplate = ({
    title,
    breadcrumbItem,
    actions,
    tableActions,
    filters,
    isLoading,
    modal,
    report: r,
    onValueClick,
}: GrossMarginReportTemplateProps) => {

    const gmPctActual = r?.grossMarginActual && r?.totalRevenueActual ? r.grossMarginActual / r.totalRevenueActual : null
    const gmPctBudget = r?.grossMarginBudget && r?.totalRevenueBudget ? r.grossMarginBudget / r.totalRevenueBudget : null
    const gmNetPctActual = r?.gmNetRevenueActual && r?.netRevenueActual ? r.gmNetRevenueActual / r.netRevenueActual : null
    const gmNetPctBudget = r?.grossMarginBudget && r?.netRevenueBudget ? r.grossMarginBudget / r.netRevenueBudget : null

    const consultingRevenueActual = useMemo(() => (r?.consultingIncomeActual ?? 0) + (r?.accruedRevenueActual ?? 0), [r])
    const consultingRevenueBudget = useMemo(() => (r?.consultingIncomeBudget ?? 0), [r])
    const consultingCogsActual = useMemo(() => (r?.directHourlyCostActual ?? 0) + (r?.subConsultantActual ?? 0), [r])
    const consultingCogsBudget = useMemo(() => (r?.directHourlyCostBudget ?? 0) + (r?.subConsultantBudget ?? 0), [r])

    const consultingRevenueGMActual = useMemo(() => consultingRevenueActual - consultingCogsActual, [r])
    const consultingRevenueGMBudget = useMemo(() => consultingRevenueBudget - consultingCogsBudget, [r])

    const p = { onValueClick }

    return (
        <div className="page-content">
            <Container fluid style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                <Breadcrumb title={title} breadcrumbItem={breadcrumbItem} actions={actions} />

                {filters && <div className="section-container">{filters}</div>}

                <div className="section-container">
                    {
                        tableActions &&
                        <div className="d-print-none" style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '10px',
                            justifyContent: 'flex-end',
                            marginBottom: '10px'
                        }}>
                            {tableActions}
                        </div>
                    }
                    {isLoading ? (
                        <LoadingState title="Loading Data" subtitle="Fetching information…" />
                    ) : (
                        <table className="gmr-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Actual</th>
                                    <th>Budget</th>
                                    <th>Variance $</th>
                                    <th>Variance %</th>
                                </tr>
                            </thead>
                            <tbody>

                                {/* REVENUE */}
                                <SectionTitle id="revenue" label="Revenue" />
                                <tr className="gmr-subsection"><td colSpan={5}>Sales Revenue</td></tr>
                                <Row {...p} field="consultingIncome" label="Consulting Income" actual={r?.consultingIncomeActual} budget={r?.consultingIncomeBudget} />
                                <Row {...p} field="disbursementIncome" label="Disbursement Income" actual={r?.disbursementIncomeActual} budget={r?.disbursementIncomeBudget} />
                                <Row {...p} field="accruedRevenue" label="Accrued Revenue" actual={r?.accruedRevenueActual} />
                                <Row {...p} field="accruedDisbursement" label="Accrued Disbursement Revenue" actual={r?.accruedDisbursementActual} />
                                <Row {...p} variant="total" field="totalSalesRevenue" label="Total Sales Revenue" actual={r?.totalSalesRevenueActual} budget={r?.totalSalesRevenueBudget} />

                                <tr className="gmr-spacer"><td colSpan={5} /></tr>

                                <tr className="gmr-subsection"><td colSpan={5}>Disbursement Cost</td></tr>
                                <Row {...p} field="directCost" label="Direct Costs" actual={r?.directCostActual} budget={r?.directCostBudget} />
                                <Row {...p} field="accruedCost" label="Accrued Cost" actual={r?.accruedCostActual} />
                                <Row {...p} variant="total" field="totalDisbursementCost" label="Total Disbursement Cost" actual={r?.totalDisbursementCostActual} budget={r?.totalDisbursementCostBudget} />
                                <Row {...p} variant="net" field="netRevenue" label="Net Revenue" actual={r?.netRevenueActual} budget={r?.netRevenueBudget} />

                                <tr className="gmr-spacer"><td colSpan={5} /></tr>

                                <tr className="gmr-subsection"><td colSpan={5}>Other Revenue</td></tr>
                                <Row {...p} field="hoSupportRevenue" label="HO Support Revenue" actual={r?.hoSupportRevenueActual} />
                                <Row {...p} field="rentalIncome" label="Rental Income" actual={r?.rentalIncomeActual} />
                                <Row {...p} variant="total" field="totalRevenue" label="Total Revenue" actual={r?.totalRevenueActual} budget={r?.totalRevenueBudget} />

                                <tr className="gmr-spacer"><td colSpan={5} /></tr>

                                {/* COST OF REVENUE */}
                                <SectionTitle id="costOfRevenue" label="Cost of Revenue" />
                                <Row {...p} field="directHourlyCost" label="Direct Hourly Cost" actual={r?.directHourlyCostActual} budget={r?.directHourlyCostBudget} />
                                <Row {...p} field="subConsultant" label="Sub-consultant" actual={r?.subConsultantActual} budget={r?.subConsultantBudget} />
                                <Row {...p} field="equipmentHour" label="Equipment Hour" actual={r?.equipmentHourActual} budget={r?.equipmentHourBudget} indent={2} />
                                <Row {...p} field="directPayrollCost" label="Direct Payroll Cost" actual={r?.directPayrollCostActual} indent={2} />
                                <Row {...p} field="accruedDirectCost" label="Accrued Direct Cost" actual={r?.accruedDirectCostActual} indent={2} />
                                <Row {...p} variant="total" field="totalCostOfRevenue" label="Total Cost of Consulting Revenue" actual={r?.totalCostOfRevenueActual} budget={r?.totalCostOfRevenueBudget} />

                                <tr className="gmr-spacer"><td colSpan={5} /></tr>

                                {/* GROSS MARGIN */}
                                <SectionTitle id="grossMargin" label="Gross Margin" />
                                <Row {...p} variant="grand" field="grossMargin" label="Gross Margin" actual={r?.grossMarginActual} budget={r?.grossMarginBudget} />
                                <Row {...p} variant="pct" field="gmPct" label="GM%" actual={gmPctActual} budget={gmPctBudget} />
                                <Row {...p} variant="net" field="gmNetRevenue" label="GM (Net Revenue)" actual={r?.gmNetRevenueActual} budget={r?.grossMarginBudget} />
                                <Row {...p} variant="pct" field="gmNetPct" label="GM % (Net Revenue)" actual={gmNetPctActual} budget={gmNetPctBudget} />

                                <tr className="gmr-spacer"><td colSpan={5} /></tr>

                                {/* CONSULTING REVENUE BREAKDOWN */}
                                <SectionTitle id="consultingBreakdown" label="Consulting Revenue Breakdown" />
                                <Row {...p} field="consultingRevenue" label="Consulting Revenue" actual={consultingRevenueActual} budget={consultingRevenueBudget} />
                                <Row {...p} field="consultingCogs" label="Consulting COGS" actual={consultingCogsActual} budget={consultingCogsBudget} />
                                <Row {...p} field="consultingRevenueGM" label="Consulting Revenue GM" actual={consultingRevenueGMActual} budget={consultingRevenueGMBudget} />
                                <Row {...p} variant="pct" field="consultingRevenueGMPct" label="Consulting Revenue GM %" actual={consultingRevenueGMActual / consultingRevenueActual} budget={consultingRevenueGMBudget / consultingRevenueBudget} />

                            </tbody>
                        </table>
                    )}
                </div>
                {/* Modal slot */}
                {modal}
            </Container>
        </div>
    )
}

export default GrossMarginReportTemplate
